import {
  type PropsWithChildren,
  type TransitionEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@dinewise/components';
import { cn } from '@dinewise/utils';
import { useLockBodyScroll } from './use-lock-body-scroll.ts';

const SHEET_EXIT_MS = 400;

type PaymentsBottomSheetProps = PropsWithChildren<{
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onApply?: () => void;
  onClear?: () => void;
  applyLabel?: string;
  clearLabel?: string;
  isApplying?: boolean;
  className?: string;
  maxHeightClass?: string;
  overlayClassName?: string;
  showFooter?: boolean;
}>;

export const PaymentsBottomSheet = ({
  isOpen,
  title,
  onClose,
  onApply,
  onClear,
  applyLabel = 'Apply Filter',
  clearLabel = 'Clear Filter',
  isApplying = false,
  className,
  maxHeightClass = 'max-h-[85vh]',
  overlayClassName = 'z-50',
  showFooter = true,
  children,
}: PaymentsBottomSheetProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isEntered, setIsEntered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const exitTimeoutRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLockBodyScroll(isRendered);

  const finishExit = () => {
    if (exitTimeoutRef.current !== null) {
      window.clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }

    setIsRendered(false);
    setIsExiting(false);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !isOpen) {
      return;
    }

    setIsExiting(false);
    setIsRendered(true);
    setIsEntered(false);
  }, [isMounted, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !isRendered) {
      return;
    }

    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    let enterFrame2 = 0;
    const enterFrame1 = requestAnimationFrame(() => {
      panel.getBoundingClientRect();
      enterFrame2 = requestAnimationFrame(() => {
        setIsEntered(true);
      });
    });

    return () => {
      cancelAnimationFrame(enterFrame1);
      cancelAnimationFrame(enterFrame2);
    };
  }, [isOpen, isRendered]);

  useEffect(() => {
    if (!isMounted || isOpen || !isRendered) {
      return;
    }

    setIsExiting(true);
    setIsEntered(false);

    exitTimeoutRef.current = window.setTimeout(finishExit, SHEET_EXIT_MS + 80);

    return () => {
      if (exitTimeoutRef.current !== null) {
        window.clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
    };
  }, [isMounted, isOpen, isRendered]);

  const handlePanelTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    if (!isOpen && isExiting) {
      finishExit();
    }
  };

  useEffect(() => {
    if (!isRendered || !isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRendered, isOpen, onClose]);

  if (!isMounted || !isRendered) {
    return null;
  }

  return createPortal(
    <div className={cn('fixed inset-0', overlayClassName)}>
      <button
        type="button"
        aria-label="Close"
        className={cn(
          'payments-bottom-sheet-backdrop absolute inset-0 bg-black/30',
          isEntered && 'is-entered',
        )}
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payments-bottom-sheet-title"
        className={cn(
          'payments-bottom-sheet-panel absolute right-0 bottom-0 left-0 flex flex-col rounded-t-3xl bg-white pt-6 pb-8',
          isEntered && 'is-entered',
          isExiting && 'is-exiting',
          maxHeightClass,
          className,
        )}
        onTransitionEnd={handlePanelTransitionEnd}
      >
        <h2
          id="payments-bottom-sheet-title"
          className="shrink-0 px-4 font-medium text-ande-black text-lg leading-[27px] tracking-[-0.5px]"
        >
          {title}
        </h2>

        <div className="payments-bottom-sheet-scrollbar min-h-0 flex-1 overflow-y-auto">
          <div className="px-4 py-4">{children}</div>
        </div>

        {showFooter ? (
          <div className="flex shrink-0 gap-3 px-4 pt-2">
            <Button
              type="button"
              theme="secondary"
              size="md"
              className="min-w-0 flex-1"
              disabled={isApplying}
              onClick={onClear}
            >
              {clearLabel}
            </Button>
            <Button
              type="button"
              theme="primary"
              size="md"
              className="min-w-0 flex-1"
              disabled={isApplying}
              onClick={onApply}
            >
              {applyLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
